<template>
    <div class="pb-12 lg:pb-32 bg-gray-100">
        <TopLocationHero static-image-key="balloon-fly___hero" title-key="balloon_fly.title" />

        <div class="relative mt-4 sm:mt-12 z-1">
            <a id="main-content" class="fixed top-0" tabindex="-1"></a>

            <div class="container hidden sm:block">
                <Breadcrumb :items="breadcrumbItems" />
            </div>

            <TopLocationBackgroundShape1 />
        </div>

        <div class="container relative z-2 mt-12">
            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- INTRO -->
            <!-- * -->
            <Type6Block
                v-if="galleryImages2"
                title-key="balloon_fly.intro.title"
                description-key="balloon_fly.intro.description"
                :images="galleryImages2"
            />

            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- AERIAL VIEW -->
            <!-- * -->
            <Type3Block
                title-key="balloon_fly.aerial_view.title"
                description-key="balloon_fly.aerial_view.description"
                static-image-key="balloon-fly___aerial-view"
                class="mt-12 sm:mt-24 lg:mt-32"
            />
        </div>

        <div class="relative">
            <TopLocationBackgroundShape2
                class="-top-64 sm:top-96 md:top-72 lg:top-52 xl:top-44 2xl:top-0 3xl:top-12 -translate-y-1/2 sm:translate-y-0 z-0"
            />

            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- FLIGHT SCHEDULE TABLE -->
            <!-- * -->
            <div id="c-schedule" class="container grid grid-cols-12 base-responsive-gap relative z-1 mt-16 lg:mt-24">
                <div class="col-span-12 lg:col-span-6 lg:col-start-2 separator-border-top-line">
                    <h2 class="header-2"><T t="balloon_fly.forecast.title" /></h2>
                    <div class="mt-4" v-html="$t('balloon_fly.forecast.description')"></div>
                </div>
                <div class="col-span-12 lg:col-span-10 lg:col-start-2 mt-4">
                    <div role="tablist" :aria-label="$t('balloon_fly.forecast.title')" class="flex">
                        <button
                            v-for="(day, i) in balloonSchedule.days"
                            :id="`balloon-fly-tabheader-${i}`"
                            :key="`schedule-day-button-${i}`"
                            class="mr-5 text-lg"
                            :aria-selected="activeSchedulePanelIndex === i ? 'true' : 'false'"
                            :aria-controls="`balloon-fly-tabpanel-${i}`"
                            role="tab"
                            :class="{ 'font-bold': activeSchedulePanelIndex === i }"
                            @click="setActiveSchedulePanelIndex(i)"
                        >
                            {{
                                i === 0
                                    ? $t('time.today')
                                    : i === 1
                                    ? $t('time.tomorrow')
                                    : $t('time.the_day_after_tomorrow')
                            }}
                        </button>
                    </div>
                    <TransitionGroup
                        name="fade"
                        tag="div"
                        class="relative overflow-hidden sm:h-72 xl:h-64"
                        :class="isSchedulePanelClosedOnMobile ? 'h-[26rem]' : 'h-[65rem]'"
                        style="transition: height 0.5s ease"
                    >
                        <div
                            v-for="(day, i) in balloonSchedule.days"
                            v-show="activeSchedulePanelIndex === i"
                            :id="`balloon-fly-tabpanel-${i}`"
                            :key="`schedule-${i}`"
                            role="tabpanel"
                            class="absolute bottom-0 w-full h-full"
                            :aria-labelledby="`balloon-fly-tabheader-${i}`"
                        >
                            <div
                                class="relative grid grid-cols-3 grid-flow-row sm:grid-rows-3 sm:grid-cols-none sm:grid-flow-col gap-2 sm:pt-8 sm:mt-8 pb-4 sm:overflow-x-auto"
                            >
                                <div
                                    v-for="(scheduleItem, j) in day.schedule"
                                    :key="`${day}-${j}`"
                                    class="flex items-center justify-center rounded relative"
                                    :class="{
                                        'sm:absolute': j === 0,
                                        'border-2': !scheduleItem.separator,
                                        'col-span-1 sm:row-span-1 sm:col-span-1': !scheduleItem.separator,
                                        'col-span-3 sm:row-span-3 sm:col-span-1': scheduleItem.separator,
                                        'border-green-400 bg-green-400': +scheduleItem.status_id === 1,
                                        'border-yellow-500 bg-yellow-500': +scheduleItem.status_id === 2,
                                        'border-error': +scheduleItem.status_id === 3,
                                        'border-blue-400': +scheduleItem.status_id === 4,
                                    }"
                                >
                                    <span
                                        v-if="scheduleItem.time"
                                        class="py-2 sm:py-3 px-3 xl:px-0 lg:text-sm 2xl:text-base"
                                    >
                                        {{ scheduleItem.time }}
                                    </span>
                                    <span v-else class="relative w-full h-12 sm:h-full">
                                        <span
                                            class="absolute top-4 sm:-top-8 sm:left-1/2 sm:-translate-x-1/2 border-t sm:border-l sm:border-t-0 border-dashed border-gray-600/50 w-full sm:w-auto"
                                            :class="{ 'sm:h-[calc(100%+2rem)]': j > 0 }"
                                        ></span>
                                        <span
                                            class="absolute top-6 left-0"
                                            :class="
                                                j === 0
                                                    ? 'sm:top-0 sm:ml-0'
                                                    : 'sm:-top-8 sm:ml-2 xl:ml-4 2xl:ml-7 3xl:ml-6'
                                            "
                                        >
                                            {{ scheduleItem.label }}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div
                            v-if="isSchedulePanelClosedOnMobile"
                            key="schedule-show-more-button"
                            class="absolute bottom-0 -left-4 w-screen flex items-end justify-center h-48 bg-gradient-to-t from-gray-100 pb-12 sm:hidden"
                        >
                            <Button
                                :label="$t('balloon_fly.show_all_time_slots')"
                                class="p-button-secondary"
                                @click.native="isSchedulePanelClosedOnMobile = false"
                            />
                        </div>
                    </TransitionGroup>

                    <div class="flex flex-col 2xl:flex-row sm:mt-4 lg:mt-0">
                        <div class="flex sm:items-center flex-col sm:flex-row">
                            <div class="schedule-status-info">
                                <span class="bg-green-400 border-green-400"></span>
                                <T t="balloon_fly.status.ideal_weather_conditions" />
                            </div>
                            <div class="schedule-status-info mt-4 sm:mt-0 sm:ml-8 lg:ml-4 xl:ml-12">
                                <span class="bg-yellow-500 border-yellow-500"></span>
                                <T t="balloon_fly.status.uncertain_weather_conditions" />
                            </div>
                        </div>
                        <div class="flex sm:items-center flex-col sm:flex-row sm:mt-4 2xl:mt-0">
                            <div class="schedule-status-info mt-4 sm:mt-0 2xl:ml-12">
                                <span class="border-error"></span>
                                <T t="balloon_fly.status.downtime" />
                            </div>
                            <div class="schedule-status-info mt-4 sm:mt-0 sm:ml-8 lg:ml-4 xl:ml-12">
                                <span class="border-blue-400"></span>
                                <T t="balloon_fly.status.private_flight" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- CHANGING WEATHER -->
            <!-- * -->
            <div class="container relative z-1 mt-16 sm:mt-20">
                <Type8Block
                    title-key="balloon_fly.changing_weather.title"
                    description-key="balloon_fly.changing_weather.description"
                />
            </div>

            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- SLIDER -->
            <!-- * -->
            <div class="container relative z-1" role="region" :aria-label="$t('balloon_fly.changing_weather.title')">
                <div class="grid grid-cols-12 base-responsive-gap mt-12 relative">
                    <div
                        class="col-span-12 sm:col-span-2 absolute sm:relative top-0 left-0 h-full py-4 sm:py-0 pl-8 sm:pl-4 lg:pl-8 z-2"
                    >
                        <div class="relative flex h-full">
                            <input
                                v-model="draggableValue"
                                type="text"
                                name="input"
                                autocomplete="off"
                                readonly
                                hidden="hidden"
                            />
                            <div class="h-full py-8 sm:py-12">
                                <div class="relative h-full z-1 border-l border-white sm:border-black">
                                    <button
                                        v-for="(value, i) in draggableValues"
                                        :key="`draggable-value-label-${i}`"
                                        class="draggable-value-label"
                                        :class="{ 'draggable-value-label--active': draggableValue === value }"
                                        :aria-selected="draggableValue === value ? 'true' : 'false'"
                                        :style="`top: ${getHeightLabelTopPercent(value)}%`"
                                        @click="() => handleSliderLabelClick(i)"
                                    >
                                        <i18n tag="span" class="offscreen" path="a11y.set_height">
                                            {{ Math.abs(value) }}
                                        </i18n>
                                        <span aria-hidden="true">{{ Math.abs(value) }} m</span>
                                    </button>
                                </div>
                            </div>
                            <div ref="area" class="relative h-full">
                                <div id="knob" class="absolute z-3">
                                    <div
                                        ref="knob"
                                        class="absolute left-0 -translate-x-1/2 w-12 h-14 sm:w-16 sm:h-20 lg:w-24 lg:h-28 bg-contain bg-center bg-no-repeat"
                                        :style="`background-image: url(${require('assets/images/illustrations/balloon.svg')})`"
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-span-12 sm:col-span-10 relative z-1">
                        <div
                            class="relative aspect-w-1 aspect-h-1 sm:aspect-w-3 sm:aspect-h-2 base-responsive-rounding"
                        >
                            <div
                                class="absolute top-0 left-0 w-32 h-full z-2"
                                style="background: linear-gradient(to right, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0) 100%)"
                            ></div>

                            <StaticImage
                                v-for="(aerialViewImageSlideKey, i) in Object.keys(aerialViewImageSlides)"
                                :key="`bird-view-img-${i}`"
                                :img-key="aerialViewImageSlides[aerialViewImageSlideKey]"
                                :image-class="`object-cover object-center absolute w-full h-full transition-opacity duration-[500ms] z-1 ${
                                    draggableValues.indexOf(draggableValue) ===
                                    Object.keys(aerialViewImageSlides).length - 1 - i
                                        ? 'opacity-1 z-1'
                                        : 'opacity-0 delay-200 z-0'
                                }`"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- 360° PANORAMA -->
            <!-- * -->
            <div class="container relative z-1 mt-16 sm:mt-20">
                <div class="grid grid-cols-12 base-responsive-gap">
                    <div class="col-span-12 lg:col-span-6 xl:col-span-4 xl:col-start-2 separator-border-top-line">
                        <h3 class="header-3 mb-4"><T t="balloon_fly.panorama.title" /></h3>
                        <div class="whitespace-pre-line">
                            <T t="balloon_fly.panorama.description" />
                        </div>
                    </div>
                    <div class="col-span-12 lg:col-span-6">
                        <div class="p-2">
                            <ClientOnly>
                                <VPannellum
                                    :src="require('assets/images/balloon-fly/panorama.jpg')"
                                    :preview="require('~/assets/images/balloon-fly/panorama-preload.jpg')"
                                    class="h-[350px] focus-visible:outline-black dark:focus-visible:outline-white base-responsive-rounding"
                                    style="outline-offset: 2px"
                                    :aria-label="$t('balloon_fly.panorama.pannellum_label')"
                                />
                            </ClientOnly>
                        </div>
                    </div>
                </div>
            </div>

            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- HISTORY CLASS... -->
            <!-- * -->
            <div class="relative z-1 mt-16 sm:mt-20">
                <div class="container relative z-1">
                    <div class="grid grid-cols-12 base-responsive-gap">
                        <div class="col-span-12 sm:col-span-6 lg:col-start-2 lg:row-span-2 order-2 sm:order-1">
                            <StaticImage
                                img-key="balloon-fly___history-1"
                                wrapper-class="base-responsive-rounding overflow-hidden"
                                image-class="w-full sm:w-auto"
                            />
                            <div class="whitespace-pre-line sm:hidden mt-8">
                                <T t="balloon_fly.did_you_know.description" />
                            </div>
                        </div>

                        <div
                            class="col-span-12 sm:col-span-6 lg:col-span-4 lg:row-span-1 separator-border-top-line 3xl:pb-28 order-1 sm:order-2"
                        >
                            <h2 class="header-2 mb-4"><T t="balloon_fly.did_you_know.title" /></h2>
                            <div class="whitespace-pre-line hidden sm:block">
                                <T t="balloon_fly.did_you_know.description" />
                            </div>
                        </div>

                        <div
                            class="col-span-6 lg:col-span-5 lg:col-start-2 xl:col-span-4 xl:row-span-2 order-4 xl:order-3"
                        >
                            <StaticImage
                                img-key="balloon-fly___history-3"
                                wrapper-class="base-responsive-rounding overflow-hidden"
                            />
                        </div>

                        <div
                            class="col-span-6 lg:col-span-5 xl:col-span-5 xl:col-start-3 xl:row-span-2 order-5 xl:order-4"
                        >
                            <StaticImage
                                img-key="balloon-fly___history-2"
                                wrapper-class="base-responsive-rounding overflow-hidden"
                            />
                        </div>

                        <div
                            class="col-span-12 lg:col-span-10 lg:col-start-2 xl:col-span-4 row-span-1 separator-border-top-line order-3 xl:order-5 mt-12 lg:mt-0"
                        >
                            <h2 class="header-2 mb-4">
                                <T t="balloon_fly.history.title" />
                            </h2>
                            <div class="whitespace-pre-line">
                                <T t="balloon_fly.history.description" />
                            </div>
                        </div>
                    </div>
                </div>

                <TopLocationBackgroundShape4 flip-y class="-bottom-12" />
            </div>
        </div>

        <!-- * -->
        <!-- * -->
        <!-- * -->
        <!-- GALLERY -->
        <!-- * -->
        <XXLGalleryCarousel v-if="galleryImages1" :images="galleryImages1" class="mt-20 lg:mt-32" />

        <div class="container mt-16 sm:mt-20">
            <TopLocationProjectPageBanner
                v-if="$t('balloon_fly.event_recommendation_button_url').length && false"
                :button-text="$t('balloon_fly.event_recommendation_button_label')"
                :button-url="$t('balloon_fly.event_recommendation_button_url')"
                label-text-key="balloon_fly.event_recommendation_text"
                type="event"
            />

            <TopLocationProjectPageBanner
                :button-text="$t('balloon_fly.visit_project_button_label')"
                :button-url="$t('balloon_fly.visit_project_button_url')"
                label-text-key="balloon_fly.visit_project_text"
                class="mt-4 sm:mt-8"
            />

            <AwardsBlock
                v-if="awards"
                :title="awards.title"
                :description="awards.description"
                :media="awards.media"
                class="mt-16 sm:mt-20"
            />
        </div>
    </div>
</template>

<script>
import { gsap } from 'gsap/dist/gsap';
import Draggable from 'gsap/dist/Draggable';
import TopLocationProjectPageBanner from '@/components/Pages/TopLocation/TopLocationProjectPageBanner.vue';
import AwardsBlock from '@/components/Blocks/AwardsBlock/AwardsBlock.vue';
import TopLocationHero from '@/components/Pages/TopLocation/Hero/Hero.vue';
import Type3Block from '@/components/Blocks/Type3Block/Type3Block.vue';
import Type6Block from '@/components/Blocks/Type6Block/Type6Block.vue';
import Type8Block from '@/components/Blocks/Type8Block/Type8Block.vue';
import XXLGalleryCarousel from '@/components/Carousels/XXLGalleryCarousel/XXLGalleryCarousel.vue';
import TopLocationBackgroundShape1 from '@/components/Pages/TopLocation/TopLocationBackgroundShape1.vue';
import TopLocationBackgroundShape2 from '@/components/Pages/TopLocation/TopLocationBackgroundShape2.vue';
import TopLocationBackgroundShape4 from '@/components/Pages/TopLocation/TopLocationBackgroundShape4.vue';

gsap.registerPlugin(Draggable);

export default {
    name: 'BalloonFly',
    components: {
        AwardsBlock,
        TopLocationProjectPageBanner,
        TopLocationBackgroundShape2,
        TopLocationBackgroundShape1,
        TopLocationBackgroundShape4,
        TopLocationHero,
        Type3Block,
        Type6Block,
        Type8Block,
        XXLGalleryCarousel,
    },
    async asyncData({ $axios, i18n }) {
        const aerialViewImageSlides = {
            '0m': 'balloon-fly___aerial-view-slide-0m',
            '50m': 'balloon-fly___aerial-view-slide-50m',
            '100m': 'balloon-fly___aerial-view-slide-100m',
            '150m': 'balloon-fly___aerial-view-slide-150m',
        };

        let galleryImages1 = null;
        let galleryImages2 = null;
        let awards = null;
        let balloonSchedule = null;

        await Promise.allSettled([
            $axios.$get('/galleries/reference/top-location___balloon-fly-1'),
            $axios.$get('/galleries/reference/top-location___balloon-fly-2'),
            $axios.$get('/awards/reference/balloon-fly'),
            $axios.$get('/balloon/table'),
        ])
            .then(([galleryImages1Res, galleryImages2Res, awardsRes, balloonScheduleRes]) => {
                if (galleryImages1Res.status === 'fulfilled') {
                    galleryImages1 = galleryImages1Res.value.slides;
                }

                if (galleryImages2Res.status === 'fulfilled') {
                    galleryImages2 = galleryImages2Res.value.slides;
                }

                if (awardsRes.status === 'fulfilled' && awardsRes.value.is_on_top_locations) {
                    awards = awardsRes.value;
                }

                if (balloonScheduleRes.status === 'fulfilled') {
                    balloonScheduleRes.value.days.map((x) => {
                        x.schedule.splice(0, 0, { separator: true, label: i18n.t('time.forenoon') });
                        x.schedule.splice(13, 0, { separator: true, label: i18n.t('time.afternoon') });
                        x.schedule.splice(35, 0, { separator: true, label: i18n.t('time.evening') });
                        return x;
                    });
                    balloonSchedule = balloonScheduleRes.value;
                }
            })
            .catch((err) => {
                console.log(err);
            });

        const breadcrumbItems = [];

        breadcrumbItems.push([i18n.t('navigation.top_locations'), 'top-locations', true]);
        breadcrumbItems.push([i18n.t('balloon_fly.title'), 'balloon-fly']);

        return {
            aerialViewImageSlides,
            breadcrumbItems,
            galleryImages1,
            galleryImages2,
            balloonSchedule,
            awards,
        };
    },
    // I couldn't figure out how to make the gsap draggable slider work with
    // values from 0 to 150, sry for the hacky workaround
    data() {
        return {
            draggable: null,
            draggableValue: 0,
            draggableValues: [-150, -100, -50, 0],
            min: -150,
            max: 0,
            activeSchedulePanelIndex: 0,
            isSchedulePanelClosedOnMobile: true,
        };
    },
    head() {
        return {
            title: this.$getPageTitle('top-locations.balloon-fly'),
            meta: this.$getPageMetaData('top-locations.balloon-fly'),
        };
    },
    watch: {
        '$store.state.windowWidth'() {
            this.draggable[0].update(true, true);

            const snapY = gsap.utils.mapRange(this.min, this.max, 0, this.draggable[0].maxY, this.draggableValue);

            gsap.to(this.draggable[0].target, { duration: 0, y: snapY });
        },
    },
    mounted() {
        // init draggable slider
        this.draggable = Draggable.create(this.$refs.knob, {
            type: 'y',
            edgeResistance: 1,
            bounds: this.$refs.area,
            throwProps: false,
            onDrag: () => {
                this.handleDrag(false);
            },
            onDragEnd: () => {
                this.handleDrag(true);
            },
        });

        gsap.to(this.draggable[0].target, { duration: 0.2, y: this.draggable[0].maxY });

        setTimeout(() => {
            if (this.$route.hash === '#schedule') {
                this.$scrollToElement(`#c-schedule`, 100);
            }
        }, 250);
    },
    beforeDestroy() {
        this.draggable[0].kill();
    },
    methods: {
        handleDrag(snap) {
            const draggable = this.draggable[0];
            const relativeValue = gsap.utils.mapRange(0, draggable.maxY, this.min, this.max, draggable.y);
            const finalValue = gsap.utils.snap(this.draggableValues, relativeValue);
            const snapY = gsap.utils.mapRange(this.min, this.max, 0, draggable.maxY, finalValue);

            if (snap) {
                gsap.to(draggable.target, { duration: 0.8, y: snapY });
            } else {
                this.draggableValue = finalValue;
            }
        },
        getHeightLabelTopPercent(value) {
            return gsap.utils.mapRange(this.min, this.max, 0, 100, value);
        },
        handleSliderLabelClick(index) {
            this.draggableValue = this.draggableValues[index];

            const draggable = this.draggable[0];
            const snapY = gsap.utils.mapRange(this.min, this.max, 0, draggable.maxY, this.draggableValue);

            gsap.to(draggable.target, { duration: 0.8, y: snapY });
        },
        setActiveSchedulePanelIndex(index) {
            this.activeSchedulePanelIndex = index;
        },
    },
};
</script>

<style scoped>
@keyframes balloon-mobile {
    from {
        transform: translate(-50%, 0.5rem);
    }
    to {
        transform: translate(-50%, -0.5rem);
    }
}

@keyframes balloon-desktop {
    from {
        transform: translate(-50%, 1rem);
    }
    to {
        transform: translate(-50%, -1rem);
    }
}

#knob {
    animation: balloon-mobile 2s ease-in-out infinite alternate;
    @media (min-width: 992px) {
        animation: balloon-desktop 2s ease-in-out infinite alternate;
    }
}

.draggable-value-label {
    @apply absolute -translate-y-1/2 left-8 lg:left-12 font-Poppins lg:text-xl xl:text-2xl font-semibold  whitespace-nowrap;
    span {
        @apply text-white sm:text-black opacity-0 sm:opacity-100 transition-opacity;
    }
    &--active span {
        @apply opacity-100;
    }
    &::after {
        content: '';
        @apply absolute block top-1/2 left-[-38px] sm:left-[-2.5rem] lg:left-[-3.5rem] w-[11px] h-[11px] sm:w-4 sm:h-4 bg-white sm:bg-black rounded-full -translate-y-1/2;
    }
}

.schedule-status-info {
    @apply flex items-center;
    & > span:first-of-type {
        @apply w-4 h-4 rounded-full border-2 mr-2;
    }
}
</style>
